import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { config } from '../../config/environment';
import './ProviderCalendar.css';

// Types for Calendar Management
interface Service {
  id: string;
  name: string;
  duration: number; // in minutes
  basePrice: number;
  isActive: boolean;
}

interface CalendarSlot {
  id: string;
  providerId: string;
  serviceId: string;
  serviceName: string;
  date: string;
  startTime: string;
  endTime: string;
  basePrice: number;
  slotPriceOverride?: number;
  isAvailable: boolean;
  slotType: 'available' | 'blocked' | 'booked';
  bookingId?: string;
  customerName?: string;
}

// New API Types for Provider Availability
interface TimeSlot {
  slotId: number;
  slotStart: string;
  slotEnd: string;
  startTime: string;
  endTime: string;
  status: 'AVAILABLE' | 'BOOKED' | 'BLOCKED';
  source: 'RECURRING' | 'OVERRIDE';
  serviceRequestId: number | null;
  bookable: boolean;
}

interface DailyAvailability {
  date: string;
  dayOfWeek: string;
  hasAvailability: boolean;
  timeSlots: TimeSlot[];
}

interface ProviderAvailabilityResponse {
  providerId: number;
  providerName: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  dailyAvailability: DailyAvailability[];
}

// Override API Types
interface TimeWindow {
  startTime: string;
  endTime: string;
}

interface OverrideRequest {
  date: string;
  overrideType: 'CUSTOM' | 'MULTI_WINDOW' | 'FULL_DAY_OFF';
  isAvailable: boolean;
  startTime?: string;
  endTime?: string;
  timeWindows?: TimeWindow[];
}

interface OverrideResponseItem {
  id: number;
  providerId: number;
  date: string;
  startTime?: string;
  endTime?: string;
  isAvailable: boolean;
  overrideType: 'CUSTOM' | 'MULTI_WINDOW' | 'FULL_DAY_OFF';
  createdAt: string;
  updatedAt: string;
  message: string;
}

interface OverrideResponse {
  overrides: OverrideResponseItem[];
  success: boolean;
  message: string;
}

const ProviderCalendar: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // State management
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedService, setSelectedService] = useState<string>('all');
  const [availabilityData, setAvailabilityData] = useState<ProviderAvailabilityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [viewMode, setViewMode] = useState<'month' | 'week'>('month');

  // Updated slot form state for overrides
  const [overrideForm, setOverrideForm] = useState({
    overrideType: 'CUSTOM' as 'CUSTOM' | 'MULTI_WINDOW' | 'FULL_DAY_OFF',
    isAvailable: true,
    startTime: '',
    endTime: '',
    timeWindows: [{ startTime: '', endTime: '' }] as TimeWindow[]
  });

  // Fetch availability data
  useEffect(() => {
    fetchAvailabilityData();
  }, [currentDate, selectedService]);

  const fetchAvailabilityData = async () => {
    if (!user?.userUuid) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('No access token found');

      const response = await fetch(
        `${config.authServerUrl}/api/provider/user/${user.userUuid}/availability`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

      const data: ProviderAvailabilityResponse = await response.json();
      console.log('Availability data fetched:', data);
      setAvailabilityData(data);
    } catch (err) {
      console.error('Error fetching availability data:', err);
      setError('Failed to load availability data');
    } finally {
      setLoading(false);
    }
  };

  const createOverride = async () => {
    if (!user?.userUuid || !selectedDate) return;

    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('No access token found');

      // Build request payload based on override type
      let payload: OverrideRequest = {
        date: selectedDate,
        overrideType: overrideForm.overrideType,
        isAvailable: overrideForm.isAvailable
      };

      if (overrideForm.overrideType === 'CUSTOM') {
        payload.startTime = overrideForm.startTime;
        payload.endTime = overrideForm.endTime;
      } else if (overrideForm.overrideType === 'MULTI_WINDOW') {
        payload.timeWindows = overrideForm.timeWindows.filter(
          window => window.startTime && window.endTime
        );
      }
      // FULL_DAY_OFF doesn't need additional fields

      const response = await fetch(
        `${config.authServerUrl}/api/provider/user/${user.userUuid}/override`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

      const result: OverrideResponse = await response.json();
      console.log('Override created:', result);
      if (result.success) {
        setShowSlotModal(false);
        resetOverrideForm();
        fetchAvailabilityData(); // Refresh availability data
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create override');
      console.error('Error creating override:', err);
    }
  };

  const resetOverrideForm = () => {
    setOverrideForm({
      overrideType: 'CUSTOM',
      isAvailable: true,
      startTime: '',
      endTime: '',
      timeWindows: [{ startTime: '', endTime: '' }]
    });
  };

  const deleteSlot = async (slotId: string) => {
    if (!user?.userUuid) return;

    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('No access token found');

      const response = await fetch(
        `${config.authServerUrl}/api/provider/${user.userUuid}/calendar/slots/${slotId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

      fetchAvailabilityData(); // Refresh availability data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete slot');
      console.error('Error deleting slot:', err);
    }
  };

  // Calendar utilities
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const formatDate = (date: Date) => {
    // Use local date formatting to avoid timezone offset issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getSlotsForDate = (date: Date) => {
    if (!availabilityData) return [];
    
    const dateStr = formatDate(date);
    const dayData = availabilityData.dailyAvailability.find(day => day.date === dateStr);
    
    if (!dayData || !dayData.hasAvailability) return [];
    
    // Convert TimeSlot[] to CalendarSlot[] format expected by the UI
    return dayData.timeSlots.map((timeSlot, index) => ({
      id: timeSlot.slotId.toString(),
      providerId: availabilityData.providerId.toString(),
      serviceId: 'general', // Default service
      serviceName: 'Available Slot',
      date: dateStr,
      startTime: timeSlot.startTime,
      endTime: timeSlot.endTime,
      basePrice: 0, // Default price
      slotPriceOverride: undefined,
      isAvailable: timeSlot.status === 'AVAILABLE',
      slotType: timeSlot.status === 'AVAILABLE' ? 'available' : 
                timeSlot.status === 'BOOKED' ? 'booked' : 'blocked' as 'available' | 'blocked' | 'booked',
      bookingId: timeSlot.serviceRequestId?.toString(),
      customerName: undefined
    }));
  };

  const hasAvailabilityForDate = (date: Date) => {
    if (!availabilityData) return false;
    const dateStr = formatDate(date);
    const dayData = availabilityData.dailyAvailability.find(day => day.date === dateStr);
    return dayData?.hasAvailability || false;
  };

  const getServicesFromAvailability = () => {
    // For now, return a default service list since the availability API 
    // doesn't contain service information
    return [
      { id: 'all', name: 'All Services', duration: 60, basePrice: 500, isActive: true },
      { id: 'vedic', name: 'Vedic Consultation', duration: 60, basePrice: 500, isActive: true },
      { id: 'astrology', name: 'Astrology Reading', duration: 45, basePrice: 750, isActive: true },
      { id: 'ritual', name: 'Ritual Guidance', duration: 90, basePrice: 1000, isActive: true }
    ];
  };

  const formatTimeRange = (startTime: string, endTime: string) => {
    // Convert 24-hour format to 12-hour format for display
    const formatTime = (time: string) => {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minutes} ${ampm}`;
    };
    
    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  };

  const getServiceName = (serviceId: string) => {
    const service = getServicesFromAvailability().find(s => s.id === serviceId);
    return service?.name || 'Unknown Service';
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(formatDate(date));
    resetOverrideForm();
    setShowSlotModal(true);
  };

  const addTimeWindow = () => {
    setOverrideForm(prev => ({
      ...prev,
      timeWindows: [...prev.timeWindows, { startTime: '', endTime: '' }]
    }));
  };

  const removeTimeWindow = (index: number) => {
    setOverrideForm(prev => ({
      ...prev,
      timeWindows: prev.timeWindows.filter((_, i) => i !== index)
    }));
  };

  const updateTimeWindow = (index: number, field: 'startTime' | 'endTime', value: string) => {
    setOverrideForm(prev => ({
      ...prev,
      timeWindows: prev.timeWindows.map((window, i) =>
        i === index ? { ...window, [field]: value } : window
      )
    }));
  };

  const handleOverrideTypeChange = (type: 'CUSTOM' | 'MULTI_WINDOW' | 'FULL_DAY_OFF') => {
    setOverrideForm(prev => ({
      ...prev,
      overrideType: type,
      isAvailable: type !== 'FULL_DAY_OFF',
      startTime: type === 'CUSTOM' ? '08:00' : '',
      endTime: type === 'CUSTOM' ? '18:00' : '',
      timeWindows: type === 'MULTI_WINDOW' ? [{ startTime: '', endTime: '' }] : []
    }));
  };

  return (
    <div className="provider-calendar">
      {/* Header */}
      <div className="calendar-header">
        <div className="header-content">
          <button className="back-btn" onClick={() => navigate('/dashboard/provider')}>
            ← Back to Dashboard
          </button>
          <div className="header-title">
            <h1>Calendar Management</h1>
            <p>Manage your service availability and bookings</p>
          </div>
          <div className="header-actions">
            <select
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value)}
              className="service-filter"
            >
              <option value="all">All Services</option>
              {getServicesFromAvailability().slice(1).map(service => (
                <option key={service.id} value={service.id}>
                  {service.name}
                </option>
              ))}
            </select>
            <div className="view-toggle">
              <button
                className={`view-btn ${viewMode === 'month' ? 'active' : ''}`}
                onClick={() => setViewMode('month')}
              >
                Month
              </button>
              <button
                className={`view-btn ${viewMode === 'week' ? 'active' : ''}`}
                onClick={() => setViewMode('week')}
              >
                Week
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Calendar Navigation */}
      <div className="calendar-nav">
        <button className="nav-btn" onClick={() => navigateMonth('prev')}>
          ‹ Previous
        </button>
        <h2 className="current-month">
          {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          {availabilityData && (
            <span className="availability-info">
              ({availabilityData.providerName} - {availabilityData.totalDays} days loaded)
            </span>
          )}
        </h2>
        <button className="nav-btn" onClick={() => navigateMonth('next')}>
          Next ›
        </button>
      </div>

      {/* Calendar Grid */}
      {loading ? (
        <div className="loading-state">
          <p>Loading calendar...</p>
        </div>
      ) : (
        <div className="calendar-container">
          {/* Weekday Headers */}
          <div className="calendar-grid">
            <div className="weekday-header">Sun</div>
            <div className="weekday-header">Mon</div>
            <div className="weekday-header">Tue</div>
            <div className="weekday-header">Wed</div>
            <div className="weekday-header">Thu</div>
            <div className="weekday-header">Fri</div>
            <div className="weekday-header">Sat</div>

            {/* Calendar Days */}
            {getDaysInMonth(currentDate).map((date, index) => (
              <div
                key={index}
                className={`calendar-day ${!date ? 'empty' : ''} ${
                  date && formatDate(date) === formatDate(new Date()) ? 'today' : ''
                } ${date && hasAvailabilityForDate(date) ? 'has-availability' : ''}`}
                onClick={() => date && handleDateClick(date)}
              >
                {date && (
                  <>
                    <div className="day-number">{date.getDate()}</div>
                    <div className="day-slots">
                      {getSlotsForDate(date).slice(0, 2).map(slot => (
                        <div
                          key={slot.id}
                          className={`slot-indicator ${slot.slotType}`}
                          title={`${slot.serviceName}`}
                        >
                          <span className="slot-icon">
                            {slot.slotType === 'booked' ? '📅' : slot.slotType === 'blocked' ? '🚫' : '✅'}
                          </span>
                          <span className="slot-time">
                            {formatTimeRange(slot.startTime, slot.endTime)}
                          </span>
                        </div>
                      ))}
                      {getSlotsForDate(date).length > 2 && (
                        <div className="more-slots">+{getSlotsForDate(date).length - 2} more</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Override Modal */}
      {showSlotModal && (
        <div className="modal-overlay" onClick={() => setShowSlotModal(false)}>
          <div className="slot-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Override Schedule</h3>
              <button className="close-btn" onClick={() => setShowSlotModal(false)}>×</button>
            </div>
            
            <div className="modal-content">
              <p className="selected-date">
                Date: {new Date(selectedDate).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>

              <div className="form-group">
                <label>Override Type</label>
                <select
                  value={overrideForm.overrideType}
                  onChange={(e) => handleOverrideTypeChange(e.target.value as 'CUSTOM' | 'MULTI_WINDOW' | 'FULL_DAY_OFF')}
                >
                  <option value="CUSTOM">Custom Time Slot</option>
                  <option value="MULTI_WINDOW">Multiple Time Windows</option>
                  <option value="FULL_DAY_OFF">Full Day Off</option>
                </select>
              </div>

              {overrideForm.overrideType === 'CUSTOM' && (
                <div className="time-inputs">
                  <div className="form-group">
                    <label>Start Time *</label>
                    <input
                      type="time"
                      value={overrideForm.startTime}
                      onChange={(e) => setOverrideForm(prev => ({ ...prev, startTime: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>End Time *</label>
                    <input
                      type="time"
                      value={overrideForm.endTime}
                      onChange={(e) => setOverrideForm(prev => ({ ...prev, endTime: e.target.value }))}
                      required
                    />
                  </div>
                </div>
              )}

              {overrideForm.overrideType === 'MULTI_WINDOW' && (
                <div className="time-windows">
                  <label>Time Windows *</label>
                  {overrideForm.timeWindows.map((window, index) => (
                    <div key={index} className="time-window">
                      <div className="time-inputs">
                        <input
                          type="time"
                          placeholder="Start time"
                          value={window.startTime}
                          onChange={(e) => updateTimeWindow(index, 'startTime', e.target.value)}
                          required
                        />
                        <input
                          type="time"
                          placeholder="End time"
                          value={window.endTime}
                          onChange={(e) => updateTimeWindow(index, 'endTime', e.target.value)}
                          required
                        />
                        {overrideForm.timeWindows.length > 1 && (
                          <button
                            type="button"
                            className="remove-window-btn"
                            onClick={() => removeTimeWindow(index)}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="add-window-btn"
                    onClick={addTimeWindow}
                  >
                    + Add Time Window
                  </button>
                </div>
              )}

              {overrideForm.overrideType === 'FULL_DAY_OFF' && (
                <div className="full-day-off-info">
                  <p>⚠️ This will mark the entire day as unavailable.</p>
                </div>
              )}

              {overrideForm.overrideType !== 'FULL_DAY_OFF' && (
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={overrideForm.isAvailable}
                      onChange={(e) => setOverrideForm(prev => ({ ...prev, isAvailable: e.target.checked }))}
                    />
                    <span className="checkbox-text">Mark as available for booking</span>
                  </label>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowSlotModal(false)}>
                Cancel
              </button>
              <button
                className="create-btn"
                onClick={createOverride}
                disabled={
                  (overrideForm.overrideType === 'CUSTOM' && (!overrideForm.startTime || !overrideForm.endTime)) ||
                  (overrideForm.overrideType === 'MULTI_WINDOW' && !overrideForm.timeWindows.some(w => w.startTime && w.endTime))
                }
              >
                Create Override
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Selected Date Details */}
      {selectedDate && !showSlotModal && (
        <div className="date-details">
          <h3>Slots for {new Date(selectedDate).toLocaleDateString()}</h3>
          <div className="slots-list">
            {getSlotsForDate(new Date(selectedDate)).map(slot => (
              <div key={slot.id} className={`slot-item ${slot.slotType}`}>
                <div className="slot-info">
                  <h4>{slot.serviceName}</h4>
                  <p>{slot.startTime} - {slot.endTime}</p>
                  <p>₹{slot.slotPriceOverride || slot.basePrice}</p>
                  {slot.customerName && <p>Customer: {slot.customerName}</p>}
                </div>
                <div className="slot-actions">
                  <span className={`status-badge ${slot.slotType}`}>
                    {slot.slotType.charAt(0).toUpperCase() + slot.slotType.slice(1)}
                  </span>
                  {slot.slotType !== 'booked' && (
                    <button
                      className="delete-slot-btn"
                      onClick={() => deleteSlot(slot.id)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
            {getSlotsForDate(new Date(selectedDate)).length === 0 && (
              <p className="no-slots">No slots available for this date.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderCalendar;